export const META_ADS_SNAPSHOT_STORAGE_KEY = 'latted.meta-ads.public-snapshot.v1';

const REQUIRED_HEADERS = ['brand', 'active_ads'];
const OPTIONAL_HEADERS = ['new_30d', 'avg_runtime_days', 'checked_at', 'source_url'];

function parseCsvLine(line) {
  const cells = [];
  let current = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const next = line[index + 1];

    if (character === '"' && quoted && next === '"') {
      current += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === ',' && !quoted) {
      cells.push(current.trim());
      current = '';
    } else {
      current += character;
    }
  }

  cells.push(current.trim());
  return cells;
}

function numberOrNull(value) {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
}

function escapeCsv(value) {
  const stringValue = String(value ?? '');
  return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
}

export function parseMetaAdsSnapshotCsv(csv, brands) {
  const lines = String(csv || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { records: {}, errors: ['Add a header row and at least one brand observation.'] };
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.toLowerCase());
  const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));
  if (missingHeaders.length) {
    return { records: {}, errors: [`Missing required column${missingHeaders.length > 1 ? 's' : ''}: ${missingHeaders.join(', ')}`] };
  }

  const brandByName = new Map(brands.map((brand) => [brand.name.toLowerCase(), brand]));
  const records = {};
  const errors = [];

  lines.slice(1).forEach((line, lineIndex) => {
    const values = parseCsvLine(line);
    const row = Object.fromEntries(headers.map((header, index) => [header, values[index] ?? '']));
    const brand = brandByName.get(row.brand.toLowerCase());
    const activeAds = numberOrNull(row.active_ads);

    if (!brand) {
      errors.push(`Row ${lineIndex + 2}: unknown brand “${row.brand || 'blank'}”.`);
      return;
    }
    if (activeAds === null) {
      errors.push(`Row ${lineIndex + 2}: active_ads must be zero or a positive number.`);
      return;
    }

    records[brand.id] = {
      activeAds,
      newAds: numberOrNull(row.new_30d),
      averageDays: numberOrNull(row.avg_runtime_days),
      checkedAt: row.checked_at || new Date().toISOString().slice(0, 10),
      sourceUrl: row.source_url || brand.metaUrl,
    };
  });

  return { records, errors };
}

export function buildMetaAdsSnapshotTemplate(brands) {
  const header = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].join(',');
  const rows = brands.map((brand) => [
    brand.name,
    '',
    '',
    '',
    '',
    brand.metaUrl,
  ].map(escapeCsv).join(','));
  return [header, ...rows].join('\n');
}

export function isFreshObservation(checkedAt, maxAgeDays = 14) {
  const timestamp = Date.parse(checkedAt);
  if (!Number.isFinite(timestamp)) return false;
  const age = Date.now() - timestamp;
  return age >= 0 && age <= maxAgeDays * 24 * 60 * 60 * 1000;
}
